import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Divider, notification, Row, Space } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { getEnvironmentService } from "../../backend";
import { trackQuickDeploy } from "../../backend/tracking/deployment";
import { trackEnvComponentRemoved } from "../../backend/tracking/environment";
import ChooseAwsAccountModal from "../../components/modals/ChooseAwsAccountModal";
import ConnectAwsAccountModal from "../../components/settings/ConnectAwsAccountModal";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { IResourceDetails, ResourceConfigurationAndStatus } from "../../types";
import { fetchAwsAccountsInfo, linkAwsAccountToStage } from "../../utils/aws-accounts";
import { isResourceActionEnabled } from "../../utils/environment";
import { getResourcesReferencingParticularResource } from "../../utils/environment/get-resource-references";

export default function ResourceSettingsDangerZone() {
    const { projectId, envId } = useParams();
    const navigate = useNavigate();
    const { resourceDetails } = useOutletContext<{ resourceDetails: IResourceDetails }>();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const [connectAwsAccountModalVisible, setConnectAwsAccountModalVisible] = useState(false);
    const [chooseAwsAccountModal, setChooseAwsAccountModal] = useState(false);
    const [awsAccountId, setAwsAccountId] = useState<string>();
    const [executeSavedAction, setExecuteSavedAction] = useState<Function>();
    const [envStatus, setEnvStatus] = useState<string>();
    const [undeployInfoMessage, setUndeployInfoMessage] = useState<string>();
    const [deleteInfoMessage, setDeleteInfoMessage] = useState<string>();
    const [referencedByResources, setReferencedByResources] = useState<string[]>();

    useEffect(() => {
        if (envId) {
            fetchEnv();
        }
    }, [envId])

    const fetchEnv = async () => {
        const { data: { resources, status } } = await getEnvironmentService().getStageDetails(envId!, projectId!);
        const mappedResourcesConfigurations = resources
            .filter(r => !["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(r.status))
            .reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr.name]: {
                        status: curr.status,
                        configurations: curr.configurations
                    }
                }
            }, {} as ResourceConfigurationAndStatus);

        // check if there are other resources that reference the one that is selected for undeployment
        const referencedByResources = getResourcesReferencingParticularResource(resourceDetails.name, mappedResourcesConfigurations!);

        setReferencedByResources(referencedByResources);
        setEnvStatus(status);
    }

    useEffect(() => {
        if (envStatus?.includes("IN_PROGRESS") && envStatus !== "REVIEW_IN_PROGRESS") {
            setDeleteInfoMessage("Resource actions are disabled while Environment deployment is in progress. Please wait for the deployment to finish before taking any actions.");
            setUndeployInfoMessage("Resource actions are disabled while Environment deployment is in progress. Please wait for the deployment to finish before taking any actions.");
        } else if (!["DELETE_COMPLETE", "NOT_DEPLOYED", "ROLLBACK_COMPLETE"].includes(resourceDetails.status)) {
            setDeleteInfoMessage("This resource is active and can not be deleted. Please undeploy it first.")
        }
    }, [envStatus])

    const undeployResource = async (resourceName: string) => {
        try {
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);

            if (stageAwsAccountId) {
                // AWS account is connected to both stage and project
                // initiate deployment with version NULL for the resource that we want to undeploy
                await getEnvironmentService().deployStage(
                    envId!,
                    projectId!,
                    {
                        partial: true,
                        resourceVersionOverrides: {
                            [resourceName]: null as any
                        }
                    });
                notification.success({
                    message: "Undeploy initiated",
                    description: "It could take a few moments for the changes to take effect."
                });
                trackQuickDeploy(envId!);
                setTimeout(() => {
                    navigate(`/projects/${projectId}/environments/${envId}/deployments`);
                }, 2000);
            } else {
                // state can be updated with a function that returns the current state, so we need to use a function that returns the function we want to set in state
                setExecuteSavedAction(() => () => undeployResource(resourceName));
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (error: any) {
            notification.error({
                message: "Error while trying to undeploy resource",
                description: error?.response?.data?.message ? error?.response?.data?.message : "We encountered an error while trying to undeploy this resource. Please try again."
            });
        }
    }
    const showUndeployConfirm = (resourceName: string) => {
        openConfirmModal({
            title: "Undeploy resource",
            description:
                <Space direction="vertical">
                    <div>
                        This action will deprovision the infrastructure assiciated with this resource from your cloud account.
                    </div>
                    <div>
                        However, the resource will remain in the environment infrastructure. You can redeploy it at any time.
                    </div>
                </Space>
            ,
            confirmation: resourceName,
            okText: "Undeploy",
            cancelText: "Cancel",
            onOk: () => { return undeployResource(resourceName); }
        })
    }

    const deleteResource = async (resourceName: string) => {
        try {
            // we actually dont need the stage to have an AWS account in order to perform this operation, but it is nice for consistency
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);

            if (stageAwsAccountId) {
                // AWS account is connected to both stage and project
                // delete resource
                await getEnvironmentService().deleteResource(envId!, resourceName, projectId!);
                notification.success({
                    message: "Resource deleted",
                    description: `The resource ${resourceName} has been successfully deleted from this environment.`
                });
                trackEnvComponentRemoved(
                    envId!,
                    resourceDetails.component.id,
                    resourceDetails.component.name,
                );
                setTimeout(() => {
                    navigate(`/projects/${projectId}/environments/${envId}/infrastructure`);
                }, 1000);
            } else {
                // state can be updated with a function that returns the current state, so we need to use a function that returns the function we want to set in state
                setExecuteSavedAction(() => () => deleteResource(resourceName));
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (error: any) {
            notification.error({
                message: "Error while trying to delete resource",
                description: error?.response?.data?.message ? error?.response?.data?.message : "We encountered an error while trying to delete this resource. Please try again."
            });
        }
    }

    const showDeleteConfirm = (resourceName: string) => {
        openConfirmModal({
            title: "Delete resource",
            description:
                <Space direction="vertical">
                    <div>
                        The resource will be permanently deleted from this environment.
                    </div>
                    <div>
                        If you just want to undeploy it, choose <span style={{ fontStyle: 'italic' }}>Undeploy</span>.
                    </div>
                    <Alert message={<b>This action cannot be undone.</b>} type="error" />
                </Space>,
            confirmation: resourceDetails.name,
            okText: "Delete Resource",
            cancelText: "Cancel",
            onOk: () => { return deleteResource(resourceName); }
        })
    }

    return (
        <>
            <Space direction="vertical" className="full-width">
                <Card bordered>
                    <Card.Meta
                        title={
                            <Space>
                                <ExclamationCircleOutlined style={{ color: 'red' }} />
                                Undeploy Resource
                            </Space>
                        }
                        description="This action will remove the infrastructure assiciated with this resource from your cloud account."
                    />
                    {
                        !!referencedByResources?.length &&
                        <Space direction="vertical">
                            <br />
                            <Alert
                                message={
                                    <div> 
                                        The <b>{resourceDetails.name}</b> resource is being referenced by some active resources. If you proceed to <b>Undeploy</b> this resource, the following resources will also be undeployed:
                                        <b> {referencedByResources.join(", ")}.</b>
                                    </div>
                                }
                                type="error"
                            />
                        </Space>
                    }
                    <Divider />
                    <Row>
                        <Col xs={24} md={20}>
                            <span style={{ fontSize: "smaller" }}>
                                {undeployInfoMessage}
                            </span>
                        </Col>
                        <Col xs={24} md={4} style={{ textAlign: "right" }}>
                            <Button
                                danger
                                disabled={!isResourceActionEnabled({
                                    action: "undeploy",
                                    resourceStatus: resourceDetails.status,
                                    envStatus: envStatus!
                                })}
                                onClick={() => showUndeployConfirm(resourceDetails.name)}
                            >
                                Undeploy
                            </Button>
                        </Col>
                    </Row>
                </Card>
                <br />
                <Card bordered>
                    <Card.Meta
                        title={
                            <Space>
                                <ExclamationCircleOutlined style={{ color: 'red' }} />
                                Delete Resource
                            </Space>
                        }
                        description="The resource will be permanently deleted, including its deployments. This action is irreversible and can not be undone."
                    />
                    <Divider />
                    <Row>
                        <Col xs={24} md={20}>
                            <span style={{ fontSize: "smaller" }}>
                                {deleteInfoMessage}
                            </span>
                        </Col>
                        <Col xs={24} md={4} style={{ textAlign: "right" }}>
                            <Button
                                type="primary"
                                danger
                                disabled={!isResourceActionEnabled({
                                    action: "delete",
                                    resourceStatus: resourceDetails.status,
                                    envStatus: envStatus!
                                })}
                                onClick={() => showDeleteConfirm(resourceDetails.name)}
                            >
                                Delete
                            </Button>
                        </Col>
                    </Row>
                </Card>
            </Space>

            {
                // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'connectAwsAccountModalVisible'
                // We have a process of periodic API calls which remain active even if when the modal closes
                connectAwsAccountModalVisible &&
                <ConnectAwsAccountModal
                    visible={connectAwsAccountModalVisible}
                    onOk={async (newAwsAccountId) => {
                        setConnectAwsAccountModalVisible(false);
                        setAwsAccountId(newAwsAccountId);
                        setChooseAwsAccountModal(true);
                    }}
                    onCancel={() => setConnectAwsAccountModalVisible(false)}
                />
            }
            {
                // This modal becomes active when the user connects an AWS account and now needs to select a region for the stage
                chooseAwsAccountModal &&
                <ChooseAwsAccountModal
                    selectedAccount={{ accountId: awsAccountId, region: undefined }}
                    visible={chooseAwsAccountModal}
                    onCancel={() => setChooseAwsAccountModal(false)}
                    onOk={async ({ accountId, region }: { accountId: string, region: string }) => {
                        await linkAwsAccountToStage({
                            envId: envId!,
                            projectId: projectId!,
                            awsAccountId: accountId,
                            awsRegion: region
                        });
                        setChooseAwsAccountModal(false);
                        executeSavedAction?.();
                    }}
                />
            }
        </>
    );
}